import * as React from "react"
import { useNavigate, useParams } from "react-router-dom"

import { useActiveBranchSlug, useActiveWorkspaceSlug } from "@/features/auth"
import { useBranchContextOverride } from "@/features/auth/hooks/use-branch-context-override"
import { FinanceContext, type FinancePageContext } from "@/features/finance/model/finance-tabs"
import { useFinanceOverviewScope } from "@/features/finance/overview/context/finance-overview-context"
import { buildBranchAdminUrl } from "@/shared/lib/branch/branch-admin-routing"
import { CreateKind, createKindFromUrl, createKindListTarget } from "@/features/create-overlay/model/create-kind"

/**
 * When a Finance Overview widget may offer to take you somewhere.
 *
 * Disabled in two scopes, for one underlying reason: a widget action can only build a URL for ONE branch,
 * so wherever the figure on screen is not that branch's, the destination and the number disagree.
 *
 * **GLOBAL (workspace)** — a consolidated widget aggregates every branch, and "view details" on a
 * church-wide figure silently dropped the reader into a single branch's page, switching their context on
 * the way.
 *
 * **INSPECTING a branch from the parish** (the Branch Details console) once had the same problem for a
 * different reason: `BranchContextOverrideProvider` points `useActiveBranchSlug` at the branch being
 * inspected, so a widget action navigated INTO that branch's own admin area — moving a parish
 * administrator into Kisangani, which is a change of who they are on the page rather than a change of
 * page. It now navigates within the parish and carries `?branchId=` instead, which the finance pages read
 * as "show me that one" (see `useFinancePageState`). The destination is filtered; the reader stays put.
 *
 * Kept out of the hook so it can be tested: the hook needs a router and a scope provider to exercise, and
 * this app has no hook-testing setup, so the decision that actually matters would have gone uncovered.
 */
export function canNavigateFromOverview(params: { context: FinancePageContext }): boolean {
  // A consolidated figure covers every branch; a URL can only name one, filtered or otherwise.
  return params.context !== FinanceContext.Global
}

/**
 * Destinations that READ `?branchId=` and narrow themselves to it.
 *
 * The other half of the inspection rule. Navigating within the parish and appending the filter only keeps
 * the promise if the page on the other end honours it — a destination that ignores the parameter shows the
 * PARISH's own figures under a number that belonged to the branch, which is the same "destination and the
 * number disagree" failure as switching context, minus the clue that you moved.
 *
 * Membership is decided by one thing: whether the page reads the parameter (`useFinancePageState`, see
 * `use-finance-page-state.ts`). Budgets and pledges are absent because they genuinely do not read it yet —
 * not as an oversight recorded here, but as the reason their widgets hide the action while inspecting.
 */
const BRANCH_FILTERABLE_ROUTES: readonly string[] = [
  "/finance/accounts",
  "/finance/transactions",
  "/finance/funds",
  "/finance/reconciliation",
  "/finance/investigations",
  "/finance/settings/opening-balances"
]

/**
 * Whether this destination narrows itself to `?branchId=`. Compared on the path, ignoring any query, except that
 * a destination opening a create (`?create=<kind>`, the create overlay over a list) never qualifies: it WRITES,
 * and a write must never be aimed at a branch the reader is only inspecting, whatever list it opens over.
 */
export function supportsBranchFilter(adminPath: string): boolean {
  if (createKindFromUrl(adminPath)) return false
  const path = adminPath.split("?")[0]
  return BRANCH_FILTERABLE_ROUTES.includes(path)
}

/**
 * Whether a widget may offer to take an inspecting reader to this particular destination.
 *
 * Per-destination rather than per-widget, so the safe path is the default: a new widget pointing at a page
 * that cannot filter hides its own action without anyone remembering to check. Hiding is what the rule asks
 * for — an action that lands on the wrong branch's data is worse than no action.
 */
export function canNavigateToFromOverview(params: {
  context: FinancePageContext
  inspecting: boolean
  adminPath: string
}): boolean {
  if (!canNavigateFromOverview({ context: params.context })) return false
  if (!params.inspecting) return true
  return supportsBranchFilter(params.adminPath)
}

/**
 * Add `?branchId=` when the destination must be narrowed to a branch the reader is not currently in.
 *
 * Appends rather than assumes: several entries in {@link OverviewRoutes} already carry a query string.
 */
export function withBranchFilter(adminPath: string, branchId: string | null): string {
  if (!branchId) return adminPath
  return `${adminPath}${adminPath.includes("?") ? "&" : "?"}branchId=${encodeURIComponent(branchId)}`
}

/**
 * Navigation helper for Finance Overview widgets. `go(adminPath)` resolves the active workspace/branch
 * (parish resolves to the "parish" slug) and navigates to the branch-admin URL, so every widget's
 * "View details" action and empty-state CTA lands on the same destination.
 *
 * Widgets check `canNavigate` — see {@link canNavigateFromOverview} for when it is false and why. `go`
 * no-ops as a backstop for any caller that forgets.
 */
export function useOverviewNav() {
  const navigate = useNavigate()
  // The route's own slug, unaffected by the inspection override — i.e. the branch the reader is actually in.
  const params = useParams<{ branchSlug: string }>()
  const branchSlug = useActiveBranchSlug()
  const workspaceSlug = useActiveWorkspaceSlug()
  const inspectedBranch = useBranchContextOverride()
  const { context } = useFinanceOverviewScope()
  const canNavigate = canNavigateFromOverview({ context })

  const canNavigateTo = React.useCallback(
    (adminPath: string) =>
      canNavigateToFromOverview({ context, inspecting: Boolean(inspectedBranch), adminPath }),
    [context, inspectedBranch],
  )

  /**
   * The URL a widget may link to, or null where it may not (same rule as `go`). For self-drawn tiles, which link
   * rather than call a handler.
   */
  const hrefFor = React.useCallback(
    (adminPath: string): string | null => {
      // Backstop for a caller that forgot to ask: a destination that cannot honour the filter is not offered.
      if (!canNavigateTo(adminPath)) return null

      /*
       * While inspecting, navigate as the branch the reader IS (the route slug, not the override) and name the
       * inspected branch in a filter. `useActiveBranchSlug` would hand back the inspected branch and put them
       * inside it, which is the whole thing this avoids.
       */
      const hostSlug = inspectedBranch ? (params?.branchSlug ?? branchSlug) : branchSlug
      const path = withBranchFilter(adminPath, inspectedBranch?.branchId ?? null)
      return buildBranchAdminUrl(hostSlug, path, workspaceSlug ?? undefined)
    },
    [branchSlug, canNavigateTo, inspectedBranch, params?.branchSlug, workspaceSlug],
  )

  const go = React.useCallback(
    (adminPath: string) => {
      const href = hrefFor(adminPath)
      if (href) navigate(href)
    },
    [hrefFor, navigate],
  )

  return Object.assign(go, { canNavigate, canNavigateTo, hrefFor })
}

/** Canonical admin-relative destinations for widget "View details" navigation. */
export const OverviewRoutes = {
  accounts: "/finance/accounts",
  transactions: "/finance/transactions",
  funds: "/finance/funds",
  budgets: "/budgets",
  pledges: "/finance/pledges",
  reconciliation: "/finance/reconciliation",
  investigations: "/finance/investigations",
  openingBalances: "/finance/settings/opening-balances",
  // Creates open as the create overlay over their list (`?create=<kind>`), keeping any `?branchId=` the list reads.
  createIncome: createKindListTarget(CreateKind.Offering),
  createExpense: createKindListTarget(CreateKind.Expense),
  createTransfer: createKindListTarget(CreateKind.Transfer),
  createPledge: createKindListTarget(CreateKind.Pledge),
  createBudget: createKindListTarget(CreateKind.Budget),
  transfers: "/finance/transfers",
} as const
